import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {alpha, useTheme} from '@mui/material/styles';
import {TransformComponent, TransformWrapper} from 'react-zoom-pan-pinch';
import {GalleryImage, photoUrl} from './GalleryImage';

const MAX_SCALE = 10;
const ZOOM_LEVELS = [1, 2.5, 4, 8];
const ZOOM_ANIMATION_MS = 200;

// Marire pe tot ecranul (pasul 21): pinch centrat pe degete, pan cu limite (mouse si touch),
// dublu-click/tap comuta zoomul, rotita; swipe intre poze doar cand poza nu e marita.
// Inchidere doar pe x (si Esc). Culorile urmeaza tema activa (luminos / nocturn / auto)
// din ThemeProvider-ul parintelui - contextul trece si prin portal.
export default function PhotoModal({photos, index = 0, title, onClose, onSelect, imageStyle, goldenFrame = false, children}) {
    const {palette} = useTheme();
    const dialog = useRef(null);
    const close = useRef(null);
    const zoom = useRef(null);
    const actions = useRef({onClose, onSelect, index});
    actions.current = {onClose, onSelect, index};

    const [scale, setScale] = useState(1);
    const pointers = useRef(new Set());
    const swipe = useRef(null);

    useEffect(() => { zoom.current?.resetTransform(0); setScale(1); }, [index]);

    useEffect(() => {
        const previousFocus = document.activeElement;
        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        close.current?.focus();
        const handleKey = (event) => {
            const current = actions.current;
            if (event.key === 'Escape') { event.preventDefault(); current.onClose(); }
            if (event.key === 'ArrowRight' && photos.length > 1 && current.onSelect) {
                event.preventDefault(); current.onSelect((current.index + 1) % photos.length);
            }
            if (event.key === 'ArrowLeft' && photos.length > 1 && current.onSelect) {
                event.preventDefault(); current.onSelect((current.index - 1 + photos.length) % photos.length);
            }
            if (event.key === 'Tab') {
                const buttons = [...dialog.current.querySelectorAll('button:not(:disabled)')];
                const first = buttons[0];
                const last = buttons[buttons.length - 1];
                if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
                else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('keydown', handleKey);
            document.body.style.overflow = overflow;
            if (previousFocus?.isConnected) previousFocus.focus();
        };
    }, [photos.length]);

    // Swipe orizontal = fotografia anterioara/urmatoare (in bucla), doar nemarita si cu un deget.
    const swipeStart = (event) => {
        pointers.current.add(event.pointerId);
        swipe.current = scale > 1.01 || pointers.current.size > 1 ? null : {x: event.clientX, y: event.clientY};
    };
    const swipeEnd = (event) => {
        pointers.current.delete(event.pointerId);
        const start = swipe.current;
        swipe.current = null;
        const {onSelect: select, index: current} = actions.current;
        if (!start || photos.length < 2 || !select) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy) * 1.2) {
            select((current + (dx < 0 ? 1 : -1) + photos.length) % photos.length);
        }
    };
    const swipeCancel = (event) => { pointers.current.delete(event.pointerId); swipe.current = null; };

    const setZoomLevel = (level) => {
        const api = zoom.current;
        if (level === 1) api?.resetTransform(ZOOM_ANIMATION_MS);
        else api?.centerView(level, ZOOM_ANIMATION_MS);
        setScale(level);
    };

    const photo = photos[index];
    if (!photo) return null;
    const label = photo.descriere || title;
    const themeVars = {
        '--modal-bg': alpha(palette.background.default, 0.97),
        '--modal-text': palette.text.primary,
        '--modal-btn-bg': palette.background.paper,
        '--modal-btn-border': palette.divider,
        '--modal-accent': palette.primary.main,
        '--modal-accent-text': palette.primary.contrastText,
        colorScheme: palette.mode,
    };
    return createPortal(
        <div className="photo-modal" ref={dialog} role="dialog" aria-modal="true" data-theme={palette.mode} style={themeVars}
             aria-label={`Fotografie mărită: ${title}`} onClick={(event) => event.stopPropagation()}>
            <button ref={close} type="button" className="photo-modal-close" aria-label="Închide fotografia" onClick={onClose}>
                <span aria-hidden="true">×</span>
            </button>
            <div className="photo-modal-zoom-controls" role="group" aria-label="Nivel de mărire">
                {ZOOM_LEVELS.map((level) => (
                    <button key={level} type="button" className={Math.abs(scale - level) < 0.05 ? 'active' : ''}
                            aria-pressed={Math.abs(scale - level) < 0.05}
                            onClick={() => setZoomLevel(level)}>
                        {level}×
                    </button>
                ))}
            </div>
            <figure className={`photo-modal-zoomable${scale > 1.01 ? ' zoomed' : ''}`}
                    onPointerDown={swipeStart} onPointerUp={swipeEnd} onPointerCancel={swipeCancel}>
                <TransformWrapper ref={zoom} minScale={1} maxScale={MAX_SCALE} limitToBounds centerZoomedOut
                                  doubleClick={{mode: 'toggle', step: 1.5}} wheel={{step: 0.15}}
                                  panning={{disabled: scale <= 1.01, velocityDisabled: true}}
                                  onTransformed={(_, state) => setScale(state.scale)}>
                    <TransformComponent wrapperClass="photo-modal-viewport"
                                        contentClass={`photo-modal-content${goldenFrame ? ' photo-modal-zoom-frame--gold' : ''}`}>
                        <GalleryImage key={photo.poza} src={photoUrl(photo.poza)} alt={label} style={imageStyle} draggable={false} />
                    </TransformComponent>
                </TransformWrapper>
                <figcaption>{label}{photos.length > 1 && ` · ${index + 1} / ${photos.length}`}</figcaption>
            </figure>
            {children && <div className="photo-modal-actions" onClick={event => event.stopPropagation()}>{children}</div>}
            {photos.length > 1 && onSelect && <>
                <button type="button" className="photo-modal-prev" aria-label="Fotografia anterioară"
                        onClick={() => onSelect((index - 1 + photos.length) % photos.length)}>‹</button>
                <button type="button" className="photo-modal-next" aria-label="Fotografia următoare"
                        onClick={() => onSelect((index + 1) % photos.length)}>›</button>
            </>}
        </div>, document.body
    );
}
